import { api } from "../configs/apiConfig";

const BASE_URL = '/desktop';

// Desktop operations
export const getAllDesktops = async () => {
    const response = await api.get(BASE_URL);
    return response.data;
};

export const getDesktopById = async (id) => {
    const response = await api.get(`${BASE_URL}/${id}`);
    return response.data;
};

export const createDesktop = async (desktopData, iconFile = null) => {
    const formData = new FormData();
    
    // Add all desktop data as individual fields
    Object.keys(desktopData).forEach(key => {
        formData.append(key, desktopData[key]);
    });
    
    // Add icon file if provided
    if (iconFile) {
        formData.append('iconImage', iconFile);
    }
    
    const response = await api.post(BASE_URL, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const updateDesktop = async (id, desktopData, iconFile = null) => {
    const formData = new FormData();
    
    // Add all desktop data
    Object.keys(desktopData).forEach(key => {
        formData.append(key, desktopData[key]);
    });

    // Add icon file if provided
    if (iconFile) {
        formData.append('iconImage', iconFile);
    }
    
    const response = await api.put(`${BASE_URL}/${id}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const deleteDesktop = async (id) => {
    await api.delete(`${BASE_URL}/${id}`);
};

export const reorderDesktops = async (desktopOrders) => {
    await api.post(`${BASE_URL}/reorder`, desktopOrders);
};

// Panel operations
export const getDesktopPanels = async (desktopId) => {
    const response = await api.get(`${BASE_URL}/${desktopId}/panels`);
    return response.data;
};

export const getPanelById = async (id) => {
    const response = await api.get(`${BASE_URL}/panels/${id}`);
    return response.data;
};

export const createPanel = async (panelData, iconFile = null) => {
    const formData = new FormData();
    
    // Add all panel data as individual fields
    Object.keys(panelData).forEach(key => {
        formData.append(key, panelData[key]);
    });

    // Add icon file if provided
    if (iconFile) {
        formData.append('iconImage', iconFile);
    }
    
    const response = await api.post(`${BASE_URL}/panels`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const updatePanel = async (id, panelData, iconFile = null) => {
    const formData = new FormData();
    
    // Add all panel data
    Object.keys(panelData).forEach(key => {
        if (key !== 'iconImage') { // Skip iconImage as we'll handle it separately
            formData.append(key, panelData[key]);
        }
    });

    // Add icon file if provided
    if (iconFile) {
        formData.append('iconImage', iconFile);
    }
    
    const response = await api.put(`${BASE_URL}/panels/${id}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const deletePanel = async (id) => {
    await api.delete(`${BASE_URL}/panels/${id}`);
};

export const updatePanelPosition = async (id, position) => {
    await api.put(`${BASE_URL}/panels/${id}/position`, position);
};

export const updatePanelSize = async (id, size) => {
    await api.put(`${BASE_URL}/panels/${id}/size`, size);
};

export const validatePanelCode = async (desktopId, code, excludePanelId = null) => {
    const url = `${BASE_URL}/panels/validate-code/${desktopId}/${code}${excludePanelId ? `/${excludePanelId}` : ''}`;
    const response = await api.get(url);
    return response.data;
};

// Get desktop by page code
export const getDesktopByPageCode = async (pageCode) => {
    const response = await api.get(`${BASE_URL}/page/${pageCode}`);
    return response.data;
};

// Get desktop by URL
export const getDesktopByUrl = async (url) => {
    const response = await api.get(`${BASE_URL}/url/${encodeURIComponent(url)}`);
    return response.data;
};

// Landing Page operations
export const getLandingItems = async (desktopId) => {
    const response = await api.get(`${BASE_URL}/${desktopId}/landing-items`);
    return response.data;
};

export const getLandingItemById = async (id) => {
    const response = await api.get(`${BASE_URL}/landing-items/${id}`);
    return response.data;
};

export const getLandingItemChildren = async (parentId) => {
    const response = await api.get(`${BASE_URL}/landing-items/${parentId}/children`);
    return response.data;
};

export const deleteLandingItem = async (id) => {
    await api.delete(`${BASE_URL}/landing-items/${id}`);
};

export const reorderLandingItems = async (itemOrders) => {
    await api.post(`${BASE_URL}/landing-items/reorder`, itemOrders);
};

export const updateLandingItem = async (id, itemData, iconFile = null, file = null) => {
    try {
        const formData = new FormData();
        
        // Add all itemData fields, but skip null values
        Object.keys(itemData).forEach(key => {
            if (itemData[key] !== null && itemData[key] !== undefined) {
                formData.append(key, itemData[key]);
            }
        });

        // Add files if provided
        if (iconFile) {
            formData.append('iconImage', iconFile);
        }
        if (file) {
            formData.append('file', file);
        }

        const response = await api.put(`${BASE_URL}/landing-items/${id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating landing item:', error);
        throw error;
    }
};

export const updateDesktopIcon = async (id, iconFile) => {
    const formData = new FormData();
    formData.append('iconImage', iconFile);
    
    const response = await api.put(`${BASE_URL}/${id}/icon`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    return response.data;
};

export const createLandingItem = async (itemData, iconFile = null, file = null) => {
    try {
        const formData = new FormData();
        
        // Add all itemData fields, but skip null values
        Object.keys(itemData).forEach(key => {
            if (itemData[key] !== null && itemData[key] !== undefined) {
                formData.append(key, itemData[key]);
            }
        });

        // Add files if provided
        if (iconFile) {
            formData.append('iconImage', iconFile);
        }
        if (file) {
            formData.append('file', file);
        }

        const response = await api.post(`${BASE_URL}/landing-items`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating landing item:', error);
        throw error;
    }
};

export const deleteDesktopIcon = async (id) => {
    const response = await api.delete(`${BASE_URL}/${id}/icon`);
    return response.data;
};

export const deletePanelIcon = async (id) => {
    const response = await api.delete(`${BASE_URL}/panels/${id}/icon`);
    return response.data;
};

export const deleteLandingItemIcon = async (id) => {
    const response = await api.delete(`${BASE_URL}/landing-items/${id}/icon`);
    return response.data;
};

const desktopService = {
    getAllDesktops,
    getDesktopById,
    createDesktop,
    updateDesktop,
    deleteDesktop,
    reorderDesktops,
    getDesktopPanels,
    getPanelById,
    createPanel,
    updatePanel,
    deletePanel,
    updatePanelPosition,
    updatePanelSize,
    getDesktopByPageCode,
    getDesktopByUrl,
    validatePanelCode,
    getLandingItems,
    getLandingItemById,
    getLandingItemChildren,
    deleteLandingItem,
    reorderLandingItems,
    updateLandingItem,
    updateDesktopIcon,
    createLandingItem,
    deleteDesktopIcon,
    deletePanelIcon,
    deleteLandingItemIcon
};

export default desktopService; 